import { belgium, checkVAT } from '@accountable/jsvat';
import { searchEntrprises } from 'api/v1/signup';
import useQuery from 'hooks/react-query/useQuery';
import useDebounce from 'hooks/useDebounce';
import { useEffect, useState } from 'react';
import { BankTransactionsCacheKeys } from 'types/cacheKeys.types';
import { AccountSettingsFieldsNamesEnum } from 'types/settings.types';
import type { Enterprise } from 'types/signup.types';
import { AvailableCountries } from 'types/users.types';

const KBO_DEBOUNCE_MS = 500;

interface UseKBOAddressParams {
  vatNumber?: string;
  streetValue?: string;
  cityValue?: string;
  zipValue?: string;
  setValue: (name: string, value: string) => void;
  setError: (name: string, error: { type: string; message: string }) => void;
  clearErrors: (name: string) => void;
  /**
   * When true, skip overwriting address fields via setValue.
   * Mismatch detection still works — compares existing form values against KBO.
   * Use on steps where address was already filled by a prior step and should not
   * be overwritten (e.g. the BE business address step after KBO prefill on activity step).
   */
  skipPrefill?: boolean;
}

const normalize = (v?: string | null) => (v ?? '').trim().toLowerCase();

/**
 * Manages KBO (Belgian business registry) address lookup for the banking onboarding form.
 *
 * ## Address field lifecycle (BE form)
 *
 * 1. On mount the form is initialised with `defaultValues` built from user settings
 *    via `constructAccountDetailsFormDefaultValues` — this is the fallback when KBO
 *    data is not yet available.
 * 2. Once this hook receives a valid VAT number (debounced, validated via `checkVAT`),
 *    it fetches the enterprise record from KBO and **overwrites** the address fields
 *    with the KBO response. KBO is the source of truth.
 *    - Fields not returned by KBO are cleared to empty string.
 * 3. If the user subsequently edits the address so it no longer matches KBO,
 *    `kboAddressMismatch` becomes `true` and the UI shows a warning alert.
 * 4. If the user changes the VAT number, a new KBO lookup is triggered (debounced)
 *    and the cycle repeats from step 2.
 *
 * ## What the hook does
 *
 * - Debounces the VAT input and validates it via `checkVAT`.
 *   If non-empty and invalid, sets a form error on the VATNumber field reactively.
 * - Fetches the enterprise record from KBO when the VAT is valid.
 * - Pre-fills the address fields (street, city, zip) with KBO data.
 * - Returns `kboAddressMismatch: true` when the current form values differ from
 *   the KBO-registered address. Suppressed while a request is in-flight or before
 *   pre-fill completes to avoid layout shift.
 *
 * @example
 * const { kboAddressMismatch } = useKBOAddress({
 *   vatNumber: VATNumberValue,
 *   streetValue, cityValue, zipValue,
 *   setValue: methods.setValue,
 *   setError: methods.setError,
 *   clearErrors: methods.clearErrors,
 * });
 */
export function useKBOAddress({
  vatNumber,
  streetValue,
  cityValue,
  zipValue,
  setValue,
  setError,
  clearErrors,
  skipPrefill,
}: UseKBOAddressParams) {
  const debouncedVAT = useDebounce(KBO_DEBOUNCE_MS, vatNumber);

  const isVATValid =
    !!debouncedVAT && checkVAT(debouncedVAT, [belgium]).isValid;

  const { data: kboEnterprise, isFetching } = useQuery<Enterprise | null>(
    [BankTransactionsCacheKeys.KBOEnterprise, debouncedVAT],
    () =>
      searchEntrprises(
        undefined,
        AvailableCountries.belgium,
        debouncedVAT,
      ).then((res) => res.data?.[0] ?? null),
    { enabled: isVATValid, staleTime: Infinity },
  );

  const [prefilledFor, setPrefilledFor] = useState<Enterprise | null>(null);

  useEffect(() => {
    if (!kboEnterprise) {
      setPrefilledFor(null);
      return;
    }

    if (!skipPrefill) {
      setValue(
        AccountSettingsFieldsNamesEnum.street,
        kboEnterprise?.street ?? '',
      );
      setValue(AccountSettingsFieldsNamesEnum.city, kboEnterprise.city ?? '');
      setValue(AccountSettingsFieldsNamesEnum.zip, kboEnterprise.zipcode ?? '');
    }
    setPrefilledFor(kboEnterprise);
  }, [kboEnterprise, setValue, skipPrefill]);

  useEffect(() => {
    const field = AccountSettingsFieldsNamesEnum.VATNumber;
    if (debouncedVAT && !isVATValid) {
      setError(field, { type: 'manual', message: 'taxes.invalid_vat_number' });
    } else {
      clearErrors(field);
    }
  }, [debouncedVAT, isVATValid, setError, clearErrors]);

  const kboAddressMismatch =
    !isFetching &&
    !!kboEnterprise &&
    prefilledFor === kboEnterprise &&
    !!(
      (kboEnterprise?.street &&
        normalize(streetValue) !== normalize(kboEnterprise.street)) ||
      (kboEnterprise?.city &&
        normalize(cityValue) !== normalize(kboEnterprise.city)) ||
      (kboEnterprise?.zipcode &&
        normalize(zipValue) !== normalize(kboEnterprise.zipcode))
    );

  return { kboAddressMismatch };
}
